"use client";

import { useQuery } from "@tanstack/react-query";
import type { MeiliThought, MindstringPrototype } from "@/generated/fastapi";
import { useMeiliRuntime } from "@/core/search";
import { useMeiliRefreshState } from "@/state/globalStore";
import type { GetSelectedThoughtDocumentsRequest } from "../api/getSelectedThoughtDocuments";
import { studioKeys } from "./keys";

export default function useSelectedThoughtRerankQuery(
  request: GetSelectedThoughtDocumentsRequest,
  rerankQuery: string | null,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const selectedPrototypes = Array.from(
    new Set(
      request.selectedThoughts.map(
        (selection) => selection.prototype as MindstringPrototype,
      ),
    ),
  );
  const activePrototype =
    selectedPrototypes.length === 1 ? selectedPrototypes[0] : undefined;

  const { searchThoughtDocuments } = useMeiliRuntime({
    surface: "studio_select",
    prototype: activePrototype,
  });
  const { meiliRefreshStamp } = useMeiliRefreshState();
  const rerankRefreshStamp = activePrototype
    ? meiliRefreshStamp["studio_thoughts"][activePrototype]
    : Object.values(meiliRefreshStamp["studio_thoughts"]).reduce(
        (sum, value) => sum + value,
        0,
      );

  return useQuery({
    queryKey: studioKeys.selectedThoughtRerank(
      request,
      rerankQuery ?? "",
      rerankRefreshStamp,
    ),
    queryFn: () =>
      searchThoughtDocuments<MeiliThought>({
        indexName: "thoughts",
        items_ids: request.selectedThoughts.flatMap(
          (selection) => selection.thoughts_ids,
        ),
        queryText: rerankQuery ?? undefined,
        semanticRatio: rerankQuery ? 1 : 0,
      }),
    enabled:
      enabled &&
      Boolean(rerankQuery) &&
      request.selectedThoughts.length > 0,
  });
}
